import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/entities/session/auth-store';

/**
 * Local-draft autosave for the full edit routes (ADR-0033). A parent who backs out of a *new*-object
 * editor mid-edit shouldn't lose their work, but we never auto-commit the record — only the explicit
 * Save mutates the server. This persists `value` to localStorage (debounced), hands back any draft
 * found on mount, and a `clearDraft()` the editor calls on a successful save or an explicit discard.
 *
 * Scope: drafts are a *new*-object convenience only (pass `enabled: false` in edit mode — a server
 * record is the source of truth there). The stored shape is opaque JSON; the caller owns the contract,
 * so a stale/incompatible draft from an older app version is tolerated by the editor's own zod parse.
 */
export interface DraftAutosave<T> {
  /** The draft found in storage on first mount (`null` when none / disabled / unparsable). */
  readonly initialDraft: T | null;
  /** Drop the persisted draft — call on successful save or an explicit "start fresh". */
  readonly clearDraft: () => void;
}

export const DRAFT_PREFIX = 'kidshome:draft:';
/** Debounce window: long enough to coalesce keystrokes, short enough that a quick back-out is safe. */
const DEBOUNCE_MS = 600;

/**
 * Namespace the localStorage key by the draft's author so it can't prefill another account's editor on
 * this shared device. We key on the signed-in member id (always present while editing — guardians only).
 * `anon` is a defensive fallback for the impossible "no user" case; such a draft simply never collides
 * with a real member's. The teardown in `use-auth-actions` clears every `kidshome:draft:` key on logout.
 */
function storageKeyFor(key: string, owner: string): string {
  return `${DRAFT_PREFIX}${owner}:${key}`;
}

/** Read + JSON-parse a draft once; never throws (storage may be unavailable or the value corrupt). */
function readDraft<T>(key: string, owner: string): T | null {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(key, owner));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function useDraftAutosave<T>(
  storageKey: string,
  value: T,
  { enabled }: { enabled: boolean },
): DraftAutosave<T> {
  // The draft's author — pinned once on mount so the initial read, every persist, and clearDraft all
  // agree on one namespaced key even if the session changes underneath (a guardian won't switch
  // accounts mid-edit). Drafts are author-private, so the member id alone fully scopes them.
  const owner = useRef(useAuthStore.getState().user?.id ?? 'anon').current;
  // Resolve the saved draft exactly once, on mount, before the first persist can overwrite it.
  const [initialDraft] = useState<T | null>(() =>
    enabled ? readDraft<T>(storageKey, owner) : null,
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `clearDraft` flips this so a queued debounced write can't resurrect a just-cleared draft.
  const cleared = useRef(false);

  const clearDraft = useCallback((): void => {
    cleared.current = true;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    try {
      window.localStorage.removeItem(storageKeyFor(storageKey, owner));
    } catch {
      // Storage unavailable (private mode / quota) — nothing to clear; the editor still saves fine.
    }
  }, [storageKey, owner]);

  useEffect(() => {
    if (!enabled || cleared.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        window.localStorage.setItem(storageKeyFor(storageKey, owner), JSON.stringify(value));
      } catch {
        // Best-effort: a failed persist (quota/private mode) silently degrades to "no draft".
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [enabled, storageKey, owner, value]);

  return { initialDraft, clearDraft };
}
